import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { analytics, AmplitudeProvider } from './components/Core/AnalyticsManager';
import { privacyConsent } from './components/Core/PrivacyConsentManager';

import AdminRouter from './components/Routers/AdminRouter';
import ProfileRouter from './components/Routers/ProfileRouter';
import RoomRouter from './components/Routers/RoomRouter';
import PlaybackRouter from './components/Routers/PlaybackRouter';

import GameStateManager from './components/Core/GameStateManager';
import ConnectionStatus from './components/Core/ConnectionStatus';
import PrivacyPolicy from './components/Core/PrivacyPolicy';
import TermsOfService from './components/Core/TermsOfService';
import RulesCarcassonne from './components/Core/RulesCarcassonne';
import FAQ from './components/Core/FAQ';
import About from './components/Core/About';
import { preWarmSpriteAlphaCache } from './components/PixiGame/spriteAlphaCacheUtils';
import PrivacyConsentModal from './components/Popups/PrivacyConsentModal';
import ChangelogModal from './components/Popups/ChangelogModal';
import VersionTracker from './utils/VersionTracker';

export const gameStateManager = new GameStateManager();

function isMobileDevice()
{
  // return true;
  return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test( navigator.userAgent );
}

export default function App( props )
{
  const [ isMobile, setIsMobile ] = useState( false );
  const isInitializedRef = useRef( false );

  useEffect( () =>
  {
    // Prevent double execution in React Strict Mode
    if( isInitializedRef.current ) return;
    isInitializedRef.current = true;

    setIsMobile( isMobileDevice() );

    console.log( "Game Loaded" );

    // Pre-warm sprite alpha cache for pixel-perfect hit detection
    // This prevents frame stuttering during gameplay when sprites are first clicked
    preWarmSpriteAlphaCache().catch( error =>
    {
      console.warn( 'Failed to pre-warm sprite alpha cache:', error );
    } );

    return () =>
    {
      // componentWillUnmount
    };
  }, [] );

  if( isMobile )
  {
    return (
      <div style={ {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: '#222',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        fontSize: '1.5rem',
        textAlign: 'center',
        padding: '2rem',
      } }>
        <img className="not-available-on-mobile-img" src={ process.env.PUBLIC_URL + '/not-available-on-mobile.png' } alt="Not available on mobile" />
        <div style={ { marginTop: '1rem' } }>
          Please play on a desktop or laptop for the best experience.
        </div>
      </div>
    );
  }

  return (
    <>
      <div><Toaster /></div>
      <BrowserRouter future={ { v7_startTransition: true, v7_relativeSplatPath: true } }>
        <AppWithRouter />
      </BrowserRouter>
    </>
  );
}

function AppWithRouter()
{
  const location = useLocation();
  const [ showPrivacyModal, setShowPrivacyModal ] = useState( false );
  const [ showChangelogModal, setShowChangelogModal ] = useState( false );
  const [ pendingChangelogVersion, setPendingChangelogVersion ] = useState( null );

  const handleConsentGiven = ( analyticsConsent ) =>
  {
    setShowPrivacyModal( false );

    // Initialize analytics if consent was given
    if( analyticsConsent )
    {
      initializeAnalytics();
    }

    // Show changelog modal if it was pending
    if( pendingChangelogVersion )
    {
      setShowChangelogModal( true );
      VersionTracker.markVersionAsSeen( pendingChangelogVersion );
    }
  };

  const handleChangelogClose = () =>
  {
    setShowChangelogModal( false );
    setPendingChangelogVersion( null );
  };

  const initializeAnalytics = () =>
  {
    if( !analytics.initialized )
    {
      const provider = new AmplitudeProvider();
      analytics.setProvider( provider );
      analytics.init( process.env.REACT_APP_AMPLITUDE_API_KEY );
      console.log( "Analytics initialized with consent" );
    }
  };

  const checkConsentRequirement = () =>
  {
    // Check if we need to show privacy consent modal
    if( privacyConsent.shouldShowConsentModal() )
    {
      setShowPrivacyModal( true );

      // If changelog should also be shown, save it for after consent
      if( VersionTracker.shouldShowChangelogAutomatically() )
      {
        setPendingChangelogVersion( VersionTracker.getCurrentVersion() );
      }
    }
    else
    {
      // No privacy modal needed
      if( privacyConsent.hasAnalyticsConsent() )
      {
        // User has already given consent, initialize analytics
        initializeAnalytics();
      }

      // Check if changelog should be shown immediately
      if( VersionTracker.shouldShowChangelogAutomatically() )
      {
        setShowChangelogModal( true );
      }
    }
  };

  // Check consent requirement on initial load
  useEffect( () =>
  {
    checkConsentRequirement();
  }, [] );

  // Check consent requirement when route changes
  useEffect( () =>
  {
    checkConsentRequirement();
  }, [ location.pathname ] );

  return (
    <>
      <ConnectionStatus gameStateManager={ gameStateManager } />

      <PrivacyConsentModal
        show={ showPrivacyModal }
        onConsentGiven={ handleConsentGiven }
      />

      <ChangelogModal
        show={ showChangelogModal }
        version={ pendingChangelogVersion || VersionTracker.getCurrentVersion() }
        onClose={ handleChangelogClose }
      />

      <Routes>
        <Route path="/" element={ <RoomRouter gameStateManager={ gameStateManager } /> } />
        <Route path="/privacy" element={ <PrivacyPolicy /> } />
        <Route path="/terms" element={ <TermsOfService /> } />
        <Route path="/rules" element={ <RulesCarcassonne /> } />
        <Route path="/faq" element={ <FAQ /> } />
        <Route path="/about" element={ <About /> } />
        <Route path="/profile/*" element={ <ProfileRouter /> } />
        <Route path="/admin" element={ <AdminRouter /> } />
        <Route path="/playback" element={ <PlaybackRouter /> } />
        {/* Catch-all route to redirect unmatched URLs to root */ }
        <Route path="*" element={ <Navigate to="/" replace /> } />
      </Routes>
    </>
  );
}
